import { useWorkspaceAccess } from 'hooks/use-workspace-access'
import { Avatar, Label, Sidebar as DSSidebar } from '@clickguard/design-system'
import { UsageCard } from 'components/usage-card'
import { ModalTypes } from 'components/basic-blocks/g-modal/types'
import { AddMemberModalBody } from 'pages/workspace/team-access/add-member/add-member'
import Globe02 from '@untitled-ui/icons-react/build/esm/Globe02'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAccountStore, useAssetStore, useUiStore, useUserStore } from 'store'
import { useShallow } from 'zustand/react/shallow'
import { PlanNames } from '../../config'
import { setItemsToLocalStorage } from '../../utils'
import { clearFiltersFromStorage } from '../../utils/filter-presistence'
import { capitalizeFirstLetter } from '../../utils/text'
import { useTrackPostHogEvent } from '../../utils/posthog'
import { ExtensionCard } from './extension-card'
import { useSidebarPages } from './use-sidebar-pages'

/**
 * The product (non-admin) rail — the canonical DS `Sidebar`.
 *
 * The rail owns the brand lockup, the collapse control, the workspace card, the
 * nav list and — since DS 0.29.0 — the bottom slot, so everything this file
 * still does is feed it: the page list comes from `useSidebarPages`, the
 * workspace payload is built here exactly as the hand-rolled sidebar built it,
 * and the Chrome Extension card is handed over as `footer`.
 */

const WebsiteFavicon = ({
	src,
	name
}: {
	src?: string | null
	name?: string
}) => {
	const [failed, setFailed] = useState(false)
	const faviconSrc =
		src ||
		(name
			? `https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&url=http://${name.replace(
					/http(s)?(:)?(\/\/)?|(\/\/)?(www\.)?/g,
					''
			  )}&size=32`
			: null)
	const wrapperClass =
		'cg-size-6 cg-rounded-full bg-bg-plan-fill cg-border border-t-checkbox cg-flex cg-items-center cg-justify-center cg-overflow-hidden cg-shrink-0'
	if (failed || !faviconSrc) {
		return (
			<div className={wrapperClass}>
				<Globe02 className="cg-size-3 cg-text-text-tertiary" />
			</div>
		)
	}
	return (
		<div className={wrapperClass}>
			<img
				src={faviconSrc}
				alt=""
				width={12}
				height={12}
				className="cg-rounded-full cg-object-contain"
				onError={() => setFailed(true)}
				onLoad={(e) => {
					if (e.currentTarget.naturalWidth < 32) setFailed(true)
				}}
			/>
		</div>
	)
}

const getFirstLetter = (name: string) =>
	name?.trim()?.charAt(0)?.toUpperCase() || ''

const normalizePlanTier = (tier: string): 'standard' | 'pro' | 'custom' => {
	if (tier === 'standard' || tier === 'custom_standard') return 'standard'
	if (tier === 'pro' || tier === 'custom_pro') return 'pro'
	if (tier === 'custom') return 'custom'
	return tier.includes('pro') ? 'pro' : 'standard'
}

const WorkspaceAvatarImage = ({ src, name }: { src: string; name: string }) => {
	const [failed, setFailed] = useState(false)
	if (failed) {
		return <Avatar initials={getFirstLetter(name)} size="xs" tone="purple" />
	}
	return (
		<img
			src={src}
			alt=""
			className="cg-size-6 cg-rounded-full cg-object-cover"
			onError={() => setFailed(true)}
		/>
	)
}

export const ProductSidebar = () => {
	const [showPlanDialog, setShowPlanDialog] = useState(false)
	const trackPostHogEvent = useTrackPostHogEvent()
	const { desktopSidebarCollapsed, collapseDesktopSidebar, setModalContent } =
		useUiStore(
			useShallow((state) => ({
				desktopSidebarCollapsed: state.desktopSidebarCollapsed,
				collapseDesktopSidebar: state.collapseDesktopSidebar,
				setModalContent: state.setModalContent
			}))
		)
	const {
		asset,
		demoMode,
		accountsWithAssets,
		setAsset,
		setAssetForId,
		setDemoMode
	} = useAssetStore(
		useShallow((state) => ({
			asset: state.asset,
			demoMode: state.demoMode,
			accountsWithAssets: state.accountsWithAssets,
			setAsset: state.setAsset,
			setAssetForId: state.setAssetForId,
			setDemoMode: state.setDemoMode
		}))
	)
	const user = useUserStore((state) => state.user)
	const {
		account,
		accounts,
		setAccount,
		setWantsToSeeDemoData,
		getSubscriptionByAccountId,
		isOwnerAccess
	} = useAccountStore(
		useShallow((state) => ({
			account: state.account,
			accounts: state.accounts,
			setAccount: state.setAccount,
			setWantsToSeeDemoData: state.setWantsToSeeDemoData,
			getSubscriptionByAccountId: state.getSubscriptionByAccountId,
			isOwnerAccess: state.isOwnerAccess
		}))
	)
	const navigate = useNavigate()

	const { pages, activeId, onSelect } = useSidebarPages()

	/* Collapse is NOT part of this condition any more: the DS footer slot is
	 * expanded-only and enforces it itself (fade out, then unmount 90ms in), so
	 * gating on `desktopSidebarCollapsed` here would just yank the card without
	 * its exit. All that's left is the product decision — has the user dismissed
	 * it. The dismissal lives in `useUserStore`, which is what makes the card a
	 * legal footer tenant: the slot's subtree is destroyed and recreated on every
	 * collapse → expand, so any state it owned locally would not survive. */
	const { access } = useWorkspaceAccess()
	const showExtensionCard =
		!user?.chrome_extension?.sidebar_card_dismissed && access !== 'not-started'

	const accountId = demoMode && !account?.id ? 'workspace-demo' : account?.id
	const assetId = demoMode && !asset?.id ? 'website-demo' : asset?.id

	const buildBadge = (acc: (typeof accounts)[0]) => {
		if (acc.status === 'INACTIVE')
			return (
				<Label color="error" size="sm">
					Inactive
				</Label>
			)
		if (
			acc.status === 'SETUP' &&
			(acc.setup?.step === 'ACCOUNT_CREATED' ||
				acc.setup?.step === 'PLAN_SELECTED')
		)
			return (
				<Label color="warning" size="sm">
					Pending activation
				</Label>
			)
		if (acc?.billing?.subscription?.type === 'TRIAL')
			return (
				<Label color="brand" size="sm">
					Trial
				</Label>
			)
		return undefined
	}

	const workspaceSwitcherData = useMemo(() => {
		return [...accounts]
			.sort((a, b) => a.name.localeCompare(b.name))
			.map((acc) => {
				const withAssets = accountsWithAssets.find((a) => a.id === acc.id)
				return {
					id: acc.id,
					name: acc.name,
					badge: buildBadge(acc),
					avatar: acc.avatar ? (
						<WorkspaceAvatarImage src={acc.avatar} name={acc.name} />
					) : (
						<Avatar
							initials={getFirstLetter(acc.name)}
							size="xs"
							tone="purple"
						/>
					),
					websites: (
						withAssets?.assets
							?.filter((asset) => asset.status === 'ACTIVE')
							.map((asset) => {
								return {
									id: asset.id,
									name: asset.name,
									icon: <WebsiteFavicon src={asset.avatar} name={asset.name} />
								}
							}) || []
					).sort((a, b) => a.name.localeCompare(b.name))
				}
			})
	}, [accounts, accountsWithAssets])

	const currentWorkspace = useMemo(() => {
		if (!account) return undefined
		const websiteCount =
			accountsWithAssets
				.find((a) => a.id === account.id)
				?.assets?.filter((a) => a.status === 'ACTIVE').length || 0
		const websitesLabel =
			websiteCount === 1 ? '1 website' : `${websiteCount} websites`
		const subscription = account?.billing?.subscription
		const planTier = subscription?.plan?.tier
		const planTierLabel =
			planTier && PlanNames[planTier]
				? PlanNames[planTier].replace(/^ClickGUARD\s+/, '')
				: planTier
				? capitalizeFirstLetter(planTier)
				: undefined
		const planName =
			subscription?.type === 'TRIAL'
				? 'Free Trial'
				: planTierLabel
				? `${planTierLabel} plan`
				: undefined
		return {
			id: account.id,
			name: account.name,
			planLabel: planName ? `${planName} • ${websitesLabel}` : websitesLabel,
			avatar: account.avatar ? (
				<WorkspaceAvatarImage src={account.avatar} name={account.name} />
			) : (
				<Avatar
					initials={getFirstLetter(account.name)}
					size="xs"
					tone="purple"
				/>
			)
		}
	}, [account, accountsWithAssets])

	const handleSelectWorkspace = useCallback(
		async (workspaceId: string) => {
			const selectedAccount = accounts.find((a) => a.id === workspaceId)
			if (!selectedAccount) return

			setAccount(selectedAccount)
			selectedAccount?.billing?.subscription &&
				getSubscriptionByAccountId(selectedAccount.id)

			const withAssets = accountsWithAssets.find((a) => a.id === workspaceId)
			const selectedAssetId = withAssets?.assets?.length
				? withAssets.assets[0].id
				: 'website-demo'

			setItemsToLocalStorage([
				{ key: 'selected_asset', value: selectedAssetId },
				{ key: 'selected_account', value: workspaceId }
			])

			clearFiltersFromStorage()
			setWantsToSeeDemoData(false)

			if (withAssets && selectedAssetId === 'website-demo') {
				await setAssetForId(withAssets.id, 'website-demo')
			} else if (withAssets?.assets?.length) {
				setDemoMode(false)
				setAsset(withAssets.assets[0])
			}

			const path = window.location.pathname.split('/')
			const pageIdentifier = path[5]
			const settingsIdentifier = path.includes('settings')
			const workspaceIdentifier = path.includes('workspace')
			const assetIdentifier = path.includes('asset')
			const settingsSubIdentifier =
				path.includes('settings') && path[path.length - 1]

			if (settingsIdentifier) {
				if (selectedAssetId === 'website-demo' && assetIdentifier) {
					navigate(`/settings/workspace/${workspaceId}/preferences`)
				} else {
					navigate(
						`/settings${
							!workspaceIdentifier && !assetIdentifier ? '/user' : ''
						}${workspaceIdentifier ? `/workspace/${workspaceId}` : ''}${
							assetIdentifier ? `/asset/${selectedAssetId}` : ''
						}/${settingsSubIdentifier}`
					)
				}
			} else {
				navigate(
					`/workspace/${workspaceId}/asset/${selectedAssetId}${
						pageIdentifier ? `/${pageIdentifier}` : ''
					}`
				)
			}
		},
		[accounts, accountsWithAssets]
	)

	const handleSelectWebsite = useCallback(
		(workspaceId: string, websiteId: string) => {
			const withAssets = accountsWithAssets.find((a) => a.id === workspaceId)
			const selectedAsset = withAssets?.assets?.find((a) => a.id === websiteId)
			if (!selectedAsset) return

			setDemoMode(false)
			setAsset(selectedAsset)

			const selectedAccount = accounts.find((a) => a.id === workspaceId)
			if (selectedAccount) {
				setAccount(selectedAccount)
				selectedAccount?.billing?.subscription &&
					getSubscriptionByAccountId(selectedAccount.id)
			}

			setItemsToLocalStorage([
				{ key: 'selected_asset', value: selectedAsset.id },
				{ key: 'selected_account', value: workspaceId }
			])

			let path = window.location.pathname
			path = path.replace(/(\/acc)[^/]+/, `/${workspaceId}`)
			path = path.replace(/(\/ast)[^/]+/, `/${websiteId}`)
			navigate(path)
		},
		[accounts, accountsWithAssets]
	)

	return (
		<DSSidebar
			pages={pages}
			// `?? ''` and never undefined: the rail treats an undefined
			// activeId as "uncontrolled" and falls back to its own state, so
			// on a page with no row of its own (settings) the last row the
			// user clicked would stay lit.
			activeId={activeId ?? ''}
			onSelect={onSelect}
			collapsed={desktopSidebarCollapsed}
			onCollapsedChange={collapseDesktopSidebar}
			// The opt-in hover trace — hovering a row redraws its glyph as one
			// continuous pen stroke. Off in the DS by default; the rail's own
			// "Icon Trace" story is what this matches.
			iconTrace
			// The rail's own bottom slot (DS 0.29.0). It sits BELOW the scrolling
			// list as a flex sibling rather than over it, so the list shortens by
			// the card's height on its own — which is what retires the measured
			// `--cg-sb-scroll-end` override this file used to carry.
			footer={showExtensionCard ? <ExtensionCard /> : undefined}
			switcher={{
				current: currentWorkspace,
				workspaces: workspaceSwitcherData,
				defaultExpandedIds: account ? [account.id] : [],
				usage: <UsageCard onExtendProtection={() => setShowPlanDialog(true)} />,
				onCreate: () => navigate('/start/standard'),
				onInvite: isOwnerAccess(user?.id)
					? () => {
							if (!account || account.id === 'workspace-demo') return
							setModalContent({
								title: 'Add Member',
								type: ModalTypes.INFO,
								actionsEnabled: false,
								showIcon: false,
								body: <AddMemberModalBody />
							})
					  }
					: undefined,
				onSelectWebsite: handleSelectWebsite,
				onSelectWorkspace: handleSelectWorkspace,
				onSettings: () => {
					navigate(
						accountId === 'workspace-demo'
							? '/settings/user/profile'
							: assetId === 'website-demo' ||
							  access !== 'full'
							? `/settings/workspace/${accountId}/preferences`
							: `/settings/workspace/${accountId}/asset/${assetId}/site-preference`
					)
				}
			}}
		/>
	)
}
